import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../utils/api';

const SettingsContext = createContext();

export const useSettings = () => useContext(SettingsContext);

// Ensure localStorage exists in case of test environment
const LOCAL_STORAGE_AVAILABLE = typeof window !== 'undefined' && window.localStorage;

// Define default settings to use before API load completes
const defaultSettings = {
  primary_color: '#3D348B',
  secondary_color: '#6c757d',
  sidebar_active_bg_color: '#3D348B',
  sidebar_active_text_color: '#ffffff',
  sidebar_bg_color: '#ffffff',
  sidebar_text_color: '#212529',
  sidebar_hover_bg_color: '#f8f9fa',
  sidebar_hover_text_color: '#3D348B',
  sidebar_width: '250',
  block_title_bg_color: '#f8f9fa',
  block_title_color: '#8B4513',
  theme_primary_color: '#3D348B',
  theme_secondary_color: '#6c757d',
  theme_accent_color: '#D67836',
  h1_color: '#212529',
  h2_color: '#212529',
  h3_color: '#212529',
  text_primary_color: '#333333',
  text_secondary_color: '#6c757d',
  tabs_bg_color: '#fafafa',
  tabs_text_color: '#212529',
  tabs_active_bg_color: '#3D348B',
  tabs_active_text_color: '#ffffff',
  tabs_hover_bg_color: '#f8f9fa',
  tabs_hover_text_color: '#3D348B',
  // Button color settings
  btn_primary_bg: '#3D348B',
  btn_primary_text: '#ffffff',
  btn_primary_hover_bg: '#2b2670',
  btn_secondary_bg: '#6c757d',
  btn_secondary_text: '#ffffff',
  btn_secondary_hover_bg: '#5a6268',
  btn_success_bg: '#198754',
  btn_success_text: '#ffffff',
  btn_success_hover_bg: '#157347',
  btn_danger_bg: '#dc3545',
  btn_danger_text: '#ffffff',
  btn_danger_hover_bg: '#bb2d3b',
  btn_warning_bg: '#ffc107',
  btn_warning_text: '#000000',
  btn_warning_hover_bg: '#ffca2c',
  btn_info_bg: '#0dcaf0',
  btn_info_text: '#000000',
  btn_info_hover_bg: '#31d2f2',
  btn_outline_border: '#3D348B',
  btn_outline_text: '#3D348B',
  btn_outline_hover_bg: '#3D348B',
  btn_outline_hover_text: '#ffffff',
  btn_disabled_bg: '#e9ecef',
  btn_disabled_text: '#6c757d',
  btn_disabled_border: '#e9ecef'
};

// Utility function to format currency values
const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2
  }).format(value);
};

// Utility function to convert hex color to RGB values
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
  }
  // Default blue fallback
  return '13, 110, 253';
};

// Utility function to determine if a color is dark (needs light text)
const isColorDark = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    // Calculate relative luminance using the formula from WCAG 2.0
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    // If luminance is less than 0.5, it's a dark color
    return luminance < 0.5;
  }
  return false;
};

// Load all settings from localStorage with app_ prefix - used only as fallback now
// eslint-disable-next-line no-unused-vars
/*
const loadSettingsFromLocalStorage = () => {
  if (!LOCAL_STORAGE_AVAILABLE) return null;
  
  try {
    // First try to load the full settings object
    const cachedSettings = localStorage.getItem('app_settings');
    if (cachedSettings) {
      const parsedSettings = JSON.parse(cachedSettings);
      return parsedSettings;
    }
    
    // If that doesn't exist, build it from individual keys
    const settings = { ...defaultSettings };
    
    // Check for individual settings in localStorage
    Object.keys(defaultSettings).forEach(key => {
      const storedValue = localStorage.getItem(`app_${key}`);
      if (storedValue !== null) {
        settings[key] = storedValue;
      }
    });
    
    return settings;
  } catch (err) {
    console.error('Error loading settings from localStorage:', err);
    return defaultSettings;
  }
};
*/

// Apply theme variables to document root
const applyThemeVariables = (settingsToApply) => {
  // Skip injecting global CSS variables on privacy policy page, support page, forgot password, and reset password pages
  if (typeof window !== 'undefined' && (
    window.location.pathname === '/privacy-policy' ||
    window.location.pathname === '/support' ||
    window.location.pathname === '/forgot-password' ||
    window.location.pathname === '/reset-password' ||
    window.location.pathname === '/landing'
  )) {
    return;
  }


  // Create or update the main theme style tag - NO INLINE STYLES ON HTML
  let themeStyleTag = document.getElementById('dynamic-theme-styles');
  if (!themeStyleTag) {
    themeStyleTag = document.createElement('style');
    themeStyleTag.id = 'dynamic-theme-styles';
    document.head.appendChild(themeStyleTag);
  }

  // Get theme colors from settings
  const primaryColor = settingsToApply.theme_primary_color || defaultSettings.theme_primary_color;
  const secondaryColor = settingsToApply.theme_secondary_color || defaultSettings.theme_secondary_color;
  const accentColor = settingsToApply.theme_accent_color || defaultSettings.theme_accent_color;
  const sidebarBgColor = settingsToApply.sidebar_bg_color || defaultSettings.sidebar_bg_color;
  const sidebarTextColor = settingsToApply.sidebar_text_color || defaultSettings.sidebar_text_color;
  const sidebarActiveBgColor = settingsToApply.sidebar_active_bg_color || primaryColor;
  const sidebarActiveTextColor = settingsToApply.sidebar_active_text_color || defaultSettings.sidebar_active_text_color;
  const sidebarHoverBgColor = settingsToApply.sidebar_hover_bg_color || defaultSettings.sidebar_hover_bg_color;
  const sidebarHoverTextColor = settingsToApply.sidebar_hover_text_color || primaryColor;
  const sidebarWidth = settingsToApply.sidebar_width || defaultSettings.sidebar_width;
  const blockTitleBgColor = settingsToApply.block_title_bg_color || defaultSettings.block_title_bg_color;
  const blockTitleColor = settingsToApply.block_title_color || defaultSettings.block_title_color;

  // Get tabs specific colors
  const tabsBgColor = settingsToApply.tabs_bg_color || defaultSettings.tabs_bg_color;
  const tabsTextColor = settingsToApply.tabs_text_color || defaultSettings.tabs_text_color;
  const tabsActiveBgColor = settingsToApply.tabs_active_bg_color || defaultSettings.tabs_active_bg_color;
  const tabsActiveTextColor = settingsToApply.tabs_active_text_color || defaultSettings.tabs_active_text_color;
  const tabsHoverBgColor = settingsToApply.tabs_hover_bg_color || defaultSettings.tabs_hover_bg_color;
  const tabsHoverTextColor = settingsToApply.tabs_hover_text_color || defaultSettings.tabs_hover_text_color;

  // Get button specific colors
  const btnPrimaryBg = settingsToApply.btn_primary_bg || defaultSettings.btn_primary_bg;
  const btnPrimaryText = settingsToApply.btn_primary_text || defaultSettings.btn_primary_text;
  const btnPrimaryHoverBg = settingsToApply.btn_primary_hover_bg || defaultSettings.btn_primary_hover_bg;
  const btnSecondaryBg = settingsToApply.btn_secondary_bg || defaultSettings.btn_secondary_bg;
  const btnSecondaryText = settingsToApply.btn_secondary_text || defaultSettings.btn_secondary_text;
  const btnSecondaryHoverBg = settingsToApply.btn_secondary_hover_bg || defaultSettings.btn_secondary_hover_bg;
  const btnSuccessBg = settingsToApply.btn_success_bg || defaultSettings.btn_success_bg;
  const btnSuccessText = settingsToApply.btn_success_text || defaultSettings.btn_success_text;
  const btnSuccessHoverBg = settingsToApply.btn_success_hover_bg || defaultSettings.btn_success_hover_bg;
  const btnDangerBg = settingsToApply.btn_danger_bg || defaultSettings.btn_danger_bg;
  const btnDangerText = settingsToApply.btn_danger_text || defaultSettings.btn_danger_text;
  const btnDangerHoverBg = settingsToApply.btn_danger_hover_bg || defaultSettings.btn_danger_hover_bg;
  const btnWarningBg = settingsToApply.btn_warning_bg || defaultSettings.btn_warning_bg;
  const btnWarningText = settingsToApply.btn_warning_text || defaultSettings.btn_warning_text;
  const btnWarningHoverBg = settingsToApply.btn_warning_hover_bg || defaultSettings.btn_warning_hover_bg;
  const btnInfoBg = settingsToApply.btn_info_bg || defaultSettings.btn_info_bg;
  const btnInfoText = settingsToApply.btn_info_text || defaultSettings.btn_info_text;
  const btnInfoHoverBg = settingsToApply.btn_info_hover_bg || defaultSettings.btn_info_hover_bg;
  const btnOutlineBorder = settingsToApply.btn_outline_border || defaultSettings.btn_outline_border;
  const btnOutlineText = settingsToApply.btn_outline_text || defaultSettings.btn_outline_text;
  const btnOutlineHoverBg = settingsToApply.btn_outline_hover_bg || defaultSettings.btn_outline_hover_bg;
  const btnOutlineHoverText = settingsToApply.btn_outline_hover_text || defaultSettings.btn_outline_hover_text;
  const btnDisabledBg = settingsToApply.btn_disabled_bg || defaultSettings.btn_disabled_bg;
  const btnDisabledText = settingsToApply.btn_disabled_text || defaultSettings.btn_disabled_text;
  const btnDisabledBorder = settingsToApply.btn_disabled_border || defaultSettings.btn_disabled_border;

  // Determine if we need to use light text on dark backgrounds for better contrast
  const blockTitleTextColor = isColorDark(blockTitleBgColor) ? '#ffffff' : blockTitleColor;
  const primaryTextColor = isColorDark(primaryColor) ? '#ffffff' : '#000000';

  // Apply comprehensive theme CSS through style tag
  themeStyleTag.innerHTML = `
    /* CSS Variables for PageTitle.css and other components */
    :root {
      --theme-primary: ${primaryColor};
      --theme-primary-color: ${primaryColor};
      --theme-primary-rgb: ${hexToRgb(primaryColor)};
      --theme-secondary: ${secondaryColor};
      --theme-accent: ${accentColor};
      --primary-color: ${primaryColor};
      --bs-primary: ${primaryColor};
      --bs-secondary: ${secondaryColor};
      --sidebar-bg-color: ${sidebarBgColor};
      --sidebar-text-color: ${sidebarTextColor};
      --sidebar-active-bg-color: ${sidebarActiveBgColor};
      --sidebar-active-text-color: ${sidebarActiveTextColor};
      --sidebar-hover-bg-color: ${sidebarHoverBgColor};
      --sidebar-hover-text-color: ${sidebarHoverTextColor};
      --sidebar-width: ${sidebarWidth}px;
      --block-title-bg-color: ${blockTitleBgColor};
      --block-title-color: ${blockTitleTextColor};
      --block-title-original-color: ${blockTitleColor};
      --tabs-bg-color: ${tabsBgColor};
      --tabs-text-color: ${tabsTextColor};
      --tabs-active-bg-color: ${tabsActiveBgColor};
      --tabs-active-text-color: ${tabsActiveTextColor};
      --tabs-hover-bg-color: ${tabsHoverBgColor};
      --tabs-hover-text-color: ${tabsHoverTextColor};
    }

    /* Primary theme colors - applied to buttons, links, etc. */
    /* Apply custom button colors to content buttons AND system buttons */
    .settings-content-area .btn-primary,
    .preview-card .btn-primary,
    .content-area .btn-primary,
    .btn-primary.custom-themed,
    .dashboard-content .btn-primary,
    .table-container .btn-primary,
    .page-title-action .btn-primary,
    .page-actions .btn-primary {
      background-color: ${btnPrimaryBg} !important;
      border-color: ${btnPrimaryBg} !important;
      color: ${btnPrimaryText} !important;
    }
    
    .settings-content-area .btn-primary:hover,
    .preview-card .btn-primary:hover,
    .content-area .btn-primary:hover,
    .btn-primary.custom-themed:hover,
    .dashboard-content .btn-primary:hover,
    .table-container .btn-primary:hover,
    .page-title-action .btn-primary:hover,
    .page-actions .btn-primary:hover,
    .settings-content-area .btn-primary:focus,
    .preview-card .btn-primary:focus,
    .content-area .btn-primary:focus,
    .btn-primary.custom-themed:focus,
    .dashboard-content .btn-primary:focus,
    .table-container .btn-primary:focus,
    .page-title-action .btn-primary:focus,
    .page-actions .btn-primary:focus,
    .settings-content-area .btn-primary:active,
    .preview-card .btn-primary:active,
    .content-area .btn-primary:active,
    .btn-primary.custom-themed:active,
    .dashboard-content .btn-primary:active,
    .table-container .btn-primary:active,
    .page-title-action .btn-primary:active,
    .page-actions .btn-primary:active {
      background-color: ${btnPrimaryHoverBg} !important;
      border-color: ${btnPrimaryHoverBg} !important;
      color: ${btnPrimaryText} !important;
    }
    
      color: #ffffff !important;
    }
    
    .text-primary,
    a.text-primary {
      color: ${primaryColor} !important;
    }
    
    a:hover,
    a:focus,
    .nav-link:hover,
    .nav-link:focus {
      color: ${primaryColor} !important;
    }
    
    /* Bootstrap form controls focus */
    .form-control:focus,
    .form-select:focus {
      border-color: ${primaryColor} !important;
      box-shadow: 0 0 0 0.2rem ${primaryColor}40 !important;
    }
    
    /* Page title styling */
    .page-title {
      color: ${primaryColor} !important;
    }
    
    /* Dashboard stat cards */
    .dashboard-stat-card {
      border-left: 4px solid ${primaryColor} !important;
    }
    
    .dashboard-stat-card .stat-value {
      color: ${primaryColor} !important;
    }
    
    /* Pagination */
    .pagination .page-link {
      color: ${primaryColor} !important;
    }
    
    .pagination .page-link:hover {
      color: ${primaryTextColor} !important;
      background-color: ${primaryColor} !important;
      border-color: ${primaryColor} !important;
    }
    
    .pagination .page-item.active .page-link {
      color: ${primaryTextColor} !important;
      background-color: ${primaryColor} !important;
      border-color: ${primaryColor} !important;
    }
    
    /* Sidebar Navigation Styles */
    .sidebar-navigation {
      background-color: ${sidebarBgColor} !important;
    }
    
    .sidebar-navigation .menu-link,
    .sidebar-navigation .menu-text,
    .sidebar-navigation .menu-icon,
    .sidebar-navigation .society-name {
      color: ${sidebarTextColor} !important;
    }
    
    .sidebar-navigation .menu-item.active,
    .sidebar-navigation li.active,
    .sidebar-navigation .active,
    .sidebar-navigation a.active {
      background-color: ${sidebarActiveBgColor} !important;
    }
    
    .sidebar-navigation .menu-item.active *,
    .sidebar-navigation li.active *,
    .sidebar-navigation .active * {
      color: ${sidebarActiveTextColor} !important;
    }
    
    .sidebar-navigation .menu-item:hover:not(.active),
    .sidebar-navigation li:hover:not(.active),
    .sidebar-navigation a:hover:not(.active) {
      background-color: ${sidebarHoverBgColor} !important;
    }
    
    .sidebar-navigation .menu-item:hover:not(.active) *,
    .sidebar-navigation li:hover:not(.active) *,
    .sidebar-navigation a:hover:not(.active) * {
      color: ${sidebarHoverTextColor} !important;
    }
    
    /* More specific selectors for sidebar hover text */
    .sidebar-navigation .menu-link:hover:not(.active) .menu-icon,
    .sidebar-navigation .menu-link:hover:not(.active) .menu-text,
    .sidebar-navigation .menu-item:hover:not(.active) .menu-icon,
    .sidebar-navigation .menu-item:hover:not(.active) .menu-text,
    .sidebar-navigation li:hover:not(.active) .menu-icon,
    .sidebar-navigation li:hover:not(.active) .menu-text {
      color: ${sidebarHoverTextColor} !important;
    }
    
    /* Extra high specificity for hover text to ensure it overrides Navbar.css */
    html body .sidebar-navigation .menu-link:hover:not(.active) .menu-icon,
    html body .sidebar-navigation .menu-link:hover:not(.active) .menu-text,
    html body .sidebar-navigation .menu-item:hover:not(.active) .menu-icon,
    html body .sidebar-navigation .menu-item:hover:not(.active) .menu-text,
    body .sidebar-navigation .menu-link:hover .menu-icon,
    body .sidebar-navigation .menu-link:hover .menu-text {
      color: ${sidebarHoverTextColor} !important;
    }
    body .sidebar-navigation .menu-item.active .menu-link:hover .menu-text,
    .sidebar-navigation .menu-item.active .menu-link:hover .menu-icon {
      color: ${sidebarActiveTextColor} !important;
    }
    /* Card headers with proper contrast */
    .card-header,
    .settings-section-header,
    .section-header,
    .block-title,
    .theme-styled-header,
    .activity-container .card-header,
    .activity-container .theme-styled-header {
      background-color: ${blockTitleBgColor} !important;
      color: ${blockTitleTextColor} !important;
    }
    
    .card-header h1, .card-header h2, .card-header h3, 
    .card-header h4, .card-header h5, .card-header h6,
    .theme-styled-header h1, .theme-styled-header h2, .theme-styled-header h3,
    .theme-styled-header h4, .theme-styled-header h5, .theme-styled-header h6,
    .activity-container .card-header h1, .activity-container .card-header h2, .activity-container .card-header h3,
    .activity-container .card-header h4, .activity-container .card-header h5, .activity-container .card-header h6 {
      color: ${blockTitleTextColor} !important;
    }
    
    /* Force light text on dark card headers - Activity Log fix */
    .card-header.bg-primary,
    .card-header[style*="background-color: ${primaryColor}"],
    .card-header[style*="background-color: #3D348B"],
    .card-header[style*="background-color: ${blockTitleBgColor}"],
    .activity-container .card-header,
    .activity-container .theme-styled-header,
    div[class*="card-header"].theme-styled-header {
      background-color: ${blockTitleBgColor} !important;
      color: ${blockTitleTextColor} !important;
    }
    
    .card-header.bg-primary h1, .card-header.bg-primary h2, .card-header.bg-primary h3,
    .card-header.bg-primary h4, .card-header.bg-primary h5, .card-header.bg-primary h6,
    .card-header.bg-primary *,
    .activity-container .card-header *,
    .activity-container .theme-styled-header *,
    div[class*="card-header"].theme-styled-header * {
      color: ${blockTitleTextColor} !important;
    }
    
    /* Badges on dark headers should remain visible */
    .card-header .badge,
    .theme-styled-header .badge,
    .activity-container .card-header .badge {
      background-color: rgba(255, 255, 255, 0.2) !important;
      color: ${blockTitleTextColor} !important;
      border: 1px solid rgba(255, 255, 255, 0.3) !important;
    }
    
    .card-header .badge.bg-primary,
    .theme-styled-header .badge.bg-primary,
    .activity-container .card-header .badge.bg-primary {
      background-color: ${primaryColor} !important;
      color: ${primaryTextColor} !important;
      border: none !important;
    }
    
    /* Tabs Navigation */
    .nav-tabs {
      border-bottom: 2px solid ${tabsActiveBgColor} !important;
      background-color: ${tabsBgColor} !important;
      padding: 0.5rem 1rem !important;
      margin-bottom: 0 !important;
      display: flex !important;
      flex-wrap: wrap !important;
      gap: 0.25rem !important;
    }
    
    .nav-tabs .nav-item {
      margin-bottom: 0 !important;
    }
    
    .nav-tabs .nav-link,
    .nav-tabs .nav-item .nav-link,
    .nav-tabs .nav-item button.nav-link {
      color: ${tabsTextColor} !important;
      background-color: ${tabsBgColor} !important;
      border: 1px solid transparent !important;
      border-radius: 6px 6px 0 0 !important;
      margin-right: 0.25rem !important;
      margin-bottom: 0 !important;
      padding: 0.75rem 1rem !important;
      font-weight: 500;
      transition: all 0.2s;
      text-decoration: none !important;
      min-width: auto !important;
      white-space: nowrap !important;
    }
    
    .nav-tabs .nav-link.active,
    .nav-tabs .nav-link.active:visited,
    .nav-tabs .nav-item .nav-link.active,
    .nav-tabs .nav-item button.nav-link.active {
      color: ${tabsActiveTextColor} !important;
      background-color: ${tabsActiveBgColor} !important;
      border-color: ${tabsActiveBgColor} !important;
      font-weight: 600 !important;
      margin-bottom: -2px !important;
      border-bottom: 2px solid ${tabsActiveBgColor} !important;
    }
    
    /* Ensure tab icons match text colors */
    .nav-tabs .nav-link i,
    .nav-tabs .nav-link .fas,
    .nav-tabs .nav-link .far,
    .nav-tabs .nav-link .fab,
    .nav-tabs .nav-item .nav-link i,
    .nav-tabs .nav-item .nav-link .fas,
    .nav-tabs .nav-item .nav-link .far,
    .nav-tabs .nav-item .nav-link .fab,
    .nav-tabs .nav-item button.nav-link i,
    .nav-tabs .nav-item button.nav-link .fas,
    .nav-tabs .nav-item button.nav-link .far,
    .nav-tabs .nav-item button.nav-link .fab {
      color: ${tabsTextColor} !important;
    }
    
    /* Active tab icons */
    .nav-tabs .nav-link.active i,
    .nav-tabs .nav-link.active .fas,
    .nav-tabs .nav-link.active .far,
    .nav-tabs .nav-link.active .fab,
    .nav-tabs .nav-item .nav-link.active i,
    .nav-tabs .nav-item .nav-link.active .fas,
    .nav-tabs .nav-item .nav-link.active .far,
    .nav-tabs .nav-item .nav-link.active .fab,
    .nav-tabs .nav-item button.nav-link.active i,
    .nav-tabs .nav-item button.nav-link.active .fas,
    .nav-tabs .nav-item button.nav-link.active .far,
    .nav-tabs .nav-item button.nav-link.active .fab {
      color: ${tabsActiveTextColor} !important;
    }
    
    .nav-tabs .nav-link:hover:not(.active),
    .nav-tabs .nav-item .nav-link:hover:not(.active),
    .nav-tabs .nav-item button.nav-link:hover:not(.active) {
      color: ${tabsHoverTextColor} !important;
      background-color: ${tabsHoverBgColor} !important;
      border-color: ${tabsHoverBgColor} !important;
      text-decoration: none !important;
    }
    
    /* Hover tab icons */
    .nav-tabs .nav-link:hover:not(.active) i,
    .nav-tabs .nav-link:hover:not(.active) .fas,
    .nav-tabs .nav-link:hover:not(.active) .far,
    .nav-tabs .nav-link:hover:not(.active) .fab,
    .nav-tabs .nav-item .nav-link:hover:not(.active) i,
    .nav-tabs .nav-item .nav-link:hover:not(.active) .fas,
    .nav-tabs .nav-item .nav-link:hover:not(.active) .far,
    .nav-tabs .nav-item .nav-link:hover:not(.active) .fab,
    .nav-tabs .nav-item button.nav-link:hover:not(.active) i,
    .nav-tabs .nav-item button.nav-link:hover:not(.active) .fas,
    .nav-tabs .nav-item button.nav-link:hover:not(.active) .far,
    .nav-tabs .nav-item button.nav-link:hover:not(.active) .fab {
      color: ${tabsHoverTextColor} !important;
    }
    
    /* Ensure all text elements in tabs match the text color */
    .nav-tabs .nav-link span,
    .nav-tabs .nav-item .nav-link span,
    .nav-tabs .nav-item button.nav-link span {
      color: inherit !important;
    }
    
    /* Make sure active tab spans inherit the active text color */
    .nav-tabs .nav-link.active span,
    .nav-tabs .nav-item .nav-link.active span,
    .nav-tabs .nav-item button.nav-link.active span {
      color: ${tabsActiveTextColor} !important;
    }
    
    /* Make sure hover tab spans inherit the hover text color */
    .nav-tabs .nav-link:hover:not(.active) span,
    .nav-tabs .nav-item .nav-link:hover:not(.active) span,
    .nav-tabs .nav-item button.nav-link:hover:not(.active) span {
      color: ${tabsHoverTextColor} !important;
    }
    
    /* Tab badges/counts styling */
    .nav-tabs .nav-link .badge,
    .nav-tabs .nav-item .nav-link .badge,
    .nav-tabs .nav-item button.nav-link .badge {
      margin-left: 0.5rem !important;
      font-size: 0.75rem !important;
      padding: 0.25rem 0.5rem !important;
      border-radius: 12px !important;
    }
    
    /* Ensure tabs don't break on smaller screens */
    @media (max-width: 768px) {
      .nav-tabs {
        padding: 0.25rem 0.5rem !important;
        gap: 0.125rem !important;
      }
      
      .nav-tabs .nav-link,
      .nav-tabs .nav-item .nav-link,
      .nav-tabs .nav-item button.nav-link {
        padding: 0.5rem 0.75rem !important;
        font-size: 0.875rem !important;
        margin-right: 0.125rem !important;
      }
    }
    
    /* Secondary elements */
    /* Only apply custom button colors to content buttons, not system buttons */
    .settings-content-area .btn-secondary,
    .preview-card .btn-secondary,
    .content-area .btn-secondary,
    .btn-secondary.custom-themed,
    .dashboard-content .btn-secondary,
    .table-container .btn-secondary {
      background-color: ${btnSecondaryBg} !important;
      border-color: ${btnSecondaryBg} !important;
      color: ${btnSecondaryText} !important;
    }
    
    .settings-content-area .btn-secondary:hover,
    .preview-card .btn-secondary:hover,
    .content-area .btn-secondary:hover,
    .btn-secondary.custom-themed:hover,
    .dashboard-content .btn-secondary:hover,
    .table-container .btn-secondary:hover,
    .settings-content-area .btn-secondary:focus,
    .preview-card .btn-secondary:focus,
    .content-area .btn-secondary:focus,
    .btn-secondary.custom-themed:focus,
    .dashboard-content .btn-secondary:focus,
    .table-container .btn-secondary:focus,
    .settings-content-area .btn-secondary:active,
    .preview-card .btn-secondary:active,
    .content-area .btn-secondary:active,
    .btn-secondary.custom-themed:active,
    .dashboard-content .btn-secondary:active,
    .table-container .btn-secondary:active {
      background-color: ${btnSecondaryHoverBg} !important;
      border-color: ${btnSecondaryHoverBg} !important;
      color: ${btnSecondaryText} !important;
    }
    
    .text-secondary {
      color: ${secondaryColor} !important;
    }
    
    /* Accent elements - now handled by specific button types */
    .text-warning {
      color: ${accentColor} !important;
    }
    
    /* Ensure active menu items maintain styling on hover */
    .sidebar-navigation .menu-item.active:hover,
    .sidebar-navigation li.active:hover,
    .sidebar-navigation .active:hover {
      background-color: ${sidebarActiveBgColor} !important;
    }
    
    .sidebar-navigation .menu-item.active:hover *,
    .sidebar-navigation li.active:hover *,
    .sidebar-navigation .active:hover * {
      color: ${sidebarActiveTextColor} !important;
    }
    
    /* Activity log specific styles */
    .activity-container .pagination .page-link {
      color: ${primaryColor} !important;
    }
    
    .activity-container .pagination .page-link:hover {
      color: white !important;
      background-color: ${primaryColor} !important;
      border-color: ${primaryColor} !important;
    }
    
    .activity-container .pagination .page-item.active .page-link {
      color: white !important;
      background-color: ${primaryColor} !important;
      border-color: ${primaryColor} !important;
    }
    
    /* Tab content area styling */
    .tab-content {
      padding: 1rem !important;
      background-color: white !important;
      border-radius: 0 0 6px 6px !important;
      border: 1px solid #dee2e6 !important;
      border-top: none !important;
    }
    
    /* Dark mode tab content */
    [data-bs-theme="dark"] .tab-content {
      background-color: #2b3035 !important;
      border-color: #495057 !important;
    }

    /* Visitor table specific button hover effects */
    .visitor-table .btn-outline-primary:hover,
    .visitor-table .btn-outline-primary:focus {
      background-color: ${primaryColor} !important;
      border-color: ${primaryColor} !important;
      color: ${primaryTextColor} !important;
    }
    
    /* All icon variations in visitor table buttons on hover */
    .visitor-table .btn-outline-primary:hover i,
    .visitor-table .btn-outline-primary:focus i,
    .visitor-table .btn-outline-primary:hover .fas,
    .visitor-table .btn-outline-primary:focus .fas,
    .visitor-table .btn-outline-primary:hover .fa,
    .visitor-table .btn-outline-primary:focus .fa,
    .visitor-table .btn-outline-primary:hover .far,
    .visitor-table .btn-outline-primary:focus .far,
    .visitor-table .btn-outline-primary:hover .fab,
    .visitor-table .btn-outline-primary:focus .fab {
      color: ${primaryTextColor} !important;
    }
    
    /* General table action button hover effects - all button types */
    table .btn-outline-primary:hover,
    table .btn-outline-primary:focus {
      background-color: ${primaryColor} !important;
      border-color: ${primaryColor} !important;
      color: ${primaryTextColor} !important;
    }
    
    table .btn-outline-primary:hover i,
    table .btn-outline-primary:focus i,
    table .btn-outline-primary:hover .fas,
    table .btn-outline-primary:focus .fas,
    table .btn-outline-primary:hover .fa,
    table .btn-outline-primary:focus .fa {
      color: ${primaryTextColor} !important;
    }
    
    /* Specific targeting for eye icon in buttons */
    .btn-outline-primary:hover .fa-eye,
    .btn-outline-primary:focus .fa-eye,
    button:hover .fa-eye,
    button:focus .fa-eye {
      color: ${primaryTextColor} !important;
    }
    
    /* Button layout - keep icon and text in one line */
    .visitor-table .btn,
    table .btn {
      white-space: nowrap !important;
      display: inline-flex !important;
      align-items: center !important;
      gap: 0.25rem !important;
    }
    
    .visitor-table .btn i,
    table .btn i {
      flex-shrink: 0 !important;
    }
    
    /* Force icon color inheritance from parent button text */
    .btn:hover i,
    .btn:focus i,
    button:hover i,
    button:focus i {
      color: inherit !important;
    }
    
    /* Ultra-specific rules for visitor table View buttons */
    .visitor-table tbody tr td .btn-outline-primary:hover,
    .visitor-table tbody tr td .btn-outline-primary:focus,
    table tbody tr td .btn-outline-primary:hover,
    table tbody tr td .btn-outline-primary:focus {
      background-color: ${primaryColor} !important;
      border-color: ${primaryColor} !important;
      color: ${primaryTextColor} !important;
    }
    
    .visitor-table tbody tr td .btn-outline-primary:hover *,
    .visitor-table tbody tr td .btn-outline-primary:focus *,
    table tbody tr td .btn-outline-primary:hover *,
    table tbody tr td .btn-outline-primary:focus * {
      color: ${primaryTextColor} !important;
    }
    
    /* Force override any existing FontAwesome color rules */
    .btn:hover .fas.fa-eye,
    .btn:focus .fas.fa-eye,
    .btn-outline-primary:hover .fas.fa-eye,
    .btn-outline-primary:focus .fas.fa-eye {
      color: ${primaryTextColor} !important;
    }
    
    /* Compact export card button styling */
    .compact-export-card .compact-btn:hover,
    .compact-export-card .compact-btn:focus {
      transform: none !important;
      transition: all 0.2s ease !important;
    }
    
    .compact-export-card .btn-outline-primary:hover,
    .compact-export-card .btn-outline-primary:focus {
      background-color: ${primaryColor} !important;
      border-color: ${primaryColor} !important;
      color: ${primaryTextColor} !important;
    }
    
    .compact-export-card .btn-outline-primary:hover i,
    .compact-export-card .btn-outline-primary:focus i {
      color: ${primaryTextColor} !important;
    }
    
    /* Ensure compact cards use theme colors properly */
    .compact-export-card .card-header {
      background-color: ${blockTitleBgColor} !important;
      color: ${blockTitleTextColor} !important;
    }

    /* Success Buttons */
    /* Only apply custom button colors to content buttons, not system buttons */
    .settings-content-area .btn-success,
    .preview-card .btn-success,
    .content-area .btn-success,
    .btn-success.custom-themed,
    .dashboard-content .btn-success,
    .table-container .btn-success {
      background-color: ${btnSuccessBg} !important;
      border-color: ${btnSuccessBg} !important;
      color: ${btnSuccessText} !important;
    }
    
    .settings-content-area .btn-success:hover,
    .preview-card .btn-success:hover,
    .content-area .btn-success:hover,
    .btn-success.custom-themed:hover,
    .dashboard-content .btn-success:hover,
    .table-container .btn-success:hover,
    .settings-content-area .btn-success:focus,
    .preview-card .btn-success:focus,
    .content-area .btn-success:focus,
    .btn-success.custom-themed:focus,
    .dashboard-content .btn-success:focus,
    .table-container .btn-success:focus,
    .settings-content-area .btn-success:active,
    .preview-card .btn-success:active,
    .content-area .btn-success:active,
    .btn-success.custom-themed:active,
    .dashboard-content .btn-success:active,
    .table-container .btn-success:active {
      background-color: ${btnSuccessHoverBg} !important;
      border-color: ${btnSuccessHoverBg} !important;
      color: ${btnSuccessText} !important;
    }

    /* Danger Buttons */
    /* Only apply custom button colors to content buttons, not system buttons */
    .settings-content-area .btn-danger,
    .preview-card .btn-danger,
    .content-area .btn-danger,
    .btn-danger.custom-themed,
    .dashboard-content .btn-danger,
    .table-container .btn-danger {
      background-color: ${btnDangerBg} !important;
      border-color: ${btnDangerBg} !important;
      color: ${btnDangerText} !important;
    }
    
    .settings-content-area .btn-danger:hover,
    .preview-card .btn-danger:hover,
    .content-area .btn-danger:hover,
    .btn-danger.custom-themed:hover,
    .dashboard-content .btn-danger:hover,
    .table-container .btn-danger:hover,
    .settings-content-area .btn-danger:focus,
    .preview-card .btn-danger:focus,
    .content-area .btn-danger:focus,
    .btn-danger.custom-themed:focus,
    .dashboard-content .btn-danger:focus,
    .table-container .btn-danger:focus,
    .settings-content-area .btn-danger:active,
    .preview-card .btn-danger:active,
    .content-area .btn-danger:active,
    .btn-danger.custom-themed:active,
    .dashboard-content .btn-danger:active,
    .table-container .btn-danger:active {
      background-color: ${btnDangerHoverBg} !important;
      border-color: ${btnDangerHoverBg} !important;
      color: ${btnDangerText} !important;
    }

    /* Warning Buttons */
    /* Only apply custom button colors to content buttons, not system buttons */
    .settings-content-area .btn-warning,
    .preview-card .btn-warning,
    .content-area .btn-warning,
    .btn-warning.custom-themed,
    .dashboard-content .btn-warning,
    .table-container .btn-warning {
      background-color: ${btnWarningBg} !important;
      border-color: ${btnWarningBg} !important;
      color: ${btnWarningText} !important;
    }
    
    .settings-content-area .btn-warning:hover,
    .preview-card .btn-warning:hover,
    .content-area .btn-warning:hover,
    .btn-warning.custom-themed:hover,
    .dashboard-content .btn-warning:hover,
    .table-container .btn-warning:hover,
    .settings-content-area .btn-warning:focus,
    .preview-card .btn-warning:focus,
    .content-area .btn-warning:focus,
    .btn-warning.custom-themed:focus,
    .dashboard-content .btn-warning:focus,
    .table-container .btn-warning:focus,
    .settings-content-area .btn-warning:active,
    .preview-card .btn-warning:active,
    .content-area .btn-warning:active,
    .btn-warning.custom-themed:active,
    .dashboard-content .btn-warning:active,
    .table-container .btn-warning:active {
      background-color: ${btnWarningHoverBg} !important;
      border-color: ${btnWarningHoverBg} !important;
      color: ${btnWarningText} !important;
    }

    /* Info Buttons */
    /* Only apply custom button colors to content buttons, not system buttons */
    .settings-content-area .btn-info,
    .preview-card .btn-info,
    .content-area .btn-info,
    .btn-info.custom-themed,
    .dashboard-content .btn-info,
    .table-container .btn-info {
      background-color: ${btnInfoBg} !important;
      border-color: ${btnInfoBg} !important;
      color: ${btnInfoText} !important;
    }
    
    .settings-content-area .btn-info:hover,
    .preview-card .btn-info:hover,
    .content-area .btn-info:hover,
    .btn-info.custom-themed:hover,
    .dashboard-content .btn-info:hover,
    .table-container .btn-info:hover,
    .settings-content-area .btn-info:focus,
    .preview-card .btn-info:focus,
    .content-area .btn-info:focus,
    .btn-info.custom-themed:focus,
    .dashboard-content .btn-info:focus,
    .table-container .btn-info:focus,
    .settings-content-area .btn-info:active,
    .preview-card .btn-info:active,
    .content-area .btn-info:active,
    .btn-info.custom-themed:active,
    .dashboard-content .btn-info:active,
    .table-container .btn-info:active {
      background-color: ${btnInfoHoverBg} !important;
      border-color: ${btnInfoHoverBg} !important;
      color: ${btnInfoText} !important;
    }

    /* Outline Buttons */
    /* Only apply custom outline button colors to content buttons, not system buttons */
    .settings-content-area .btn-outline-primary,
    .preview-card .btn-outline-primary,
    .content-area .btn-outline-primary,
    .btn-outline-primary.custom-themed,
    .dashboard-content .btn-outline-primary,
    .table-container .btn-outline-primary {
      background-color: transparent !important;
      border-color: ${btnOutlineBorder} !important;
      color: ${btnOutlineText} !important;
    }
    
    .settings-content-area .btn-outline-primary:hover,
    .preview-card .btn-outline-primary:hover,
    .content-area .btn-outline-primary:hover,
    .btn-outline-primary.custom-themed:hover,
    .dashboard-content .btn-outline-primary:hover,
    .table-container .btn-outline-primary:hover,
    .settings-content-area .btn-outline-primary:focus,
    .preview-card .btn-outline-primary:focus,
    .content-area .btn-outline-primary:focus,
    .btn-outline-primary.custom-themed:focus,
    .dashboard-content .btn-outline-primary:focus,
    .table-container .btn-outline-primary:focus,
    .settings-content-area .btn-outline-primary:active,
    .preview-card .btn-outline-primary:active,
    .content-area .btn-outline-primary:active,
    .btn-outline-primary.custom-themed:active,
    .dashboard-content .btn-outline-primary:active,
    .table-container .btn-outline-primary:active {
      background-color: ${btnOutlineHoverBg} !important;
      border-color: ${btnOutlineHoverBg} !important;
      color: ${btnOutlineHoverText} !important;
    }
    
    .btn-outline-secondary {
      background-color: transparent !important;
      border-color: ${btnSecondaryBg} !important;
      color: ${btnSecondaryBg} !important;
    }
    
    .btn-outline-secondary:hover,
    .btn-outline-secondary:focus,
    .btn-outline-secondary:active,
    .btn-outline-secondary.active {
      background-color: ${btnSecondaryBg} !important;
      border-color: ${btnSecondaryBg} !important;
      color: ${btnSecondaryText} !important;
    }
    
    .btn-outline-success {
      background-color: transparent !important;
      border-color: ${btnSuccessBg} !important;
      color: ${btnSuccessBg} !important;
    }
    
    .btn-outline-success:hover,
    .btn-outline-success:focus,
    .btn-outline-success:active,
    .btn-outline-success.active {
      background-color: ${btnSuccessBg} !important;
      border-color: ${btnSuccessBg} !important;
      color: ${btnSuccessText} !important;
    }
    
    .btn-outline-danger {
      background-color: transparent !important;
      border-color: ${btnDangerBg} !important;
      color: ${btnDangerBg} !important;
    }
    
    .btn-outline-danger:hover,
    .btn-outline-danger:focus,
    .btn-outline-danger:active,
    .btn-outline-danger.active {
      background-color: ${btnDangerBg} !important;
      border-color: ${btnDangerBg} !important;
      color: ${btnDangerText} !important;
    }
    
    .btn-outline-warning {
      background-color: transparent !important;
      border-color: ${btnWarningBg} !important;
      color: ${btnWarningBg} !important;
    }
    
    .btn-outline-warning:hover,
    .btn-outline-warning:focus,
    .btn-outline-warning:active,
    .btn-outline-warning.active {
      background-color: ${btnWarningBg} !important;
      border-color: ${btnWarningBg} !important;
      color: ${btnWarningText} !important;
    }
    
    .btn-outline-info {
      background-color: transparent !important;
      border-color: ${btnInfoBg} !important;
      color: ${btnInfoBg} !important;
    }
    
    .btn-outline-info:hover,
    .btn-outline-info:focus,
    .btn-outline-info:active,
    .btn-outline-info.active {
      background-color: ${btnInfoBg} !important;
      border-color: ${btnInfoBg} !important;
      color: ${btnInfoText} !important;
    }
    
    /* Disabled Buttons */
    .btn:disabled,
    .btn.disabled,
    fieldset:disabled .btn {
      background-color: ${btnDisabledBg} !important;
      border-color: ${btnDisabledBorder} !important;
      color: ${btnDisabledText} !important;
      opacity: 0.65 !important;
    }
    
    /* Ensure button icons inherit colors */
    .btn i,
    .btn .fas,
    .btn .far,
    .btn .fab,
    .btn .fa {
      color: inherit !important;
    }
    
    /* Button focus states */
    .btn:focus,
    .btn.focus {
      box-shadow: 0 0 0 0.2rem rgba(${hexToRgb(btnPrimaryBg)}, 0.25) !important;
    }
    
    .btn-secondary:focus,
    .btn-secondary.focus {
      box-shadow: 0 0 0 0.2rem rgba(${hexToRgb(btnSecondaryBg)}, 0.25) !important;
    }
    
    .btn-success:focus,
    .btn-success.focus {
      box-shadow: 0 0 0 0.2rem rgba(${hexToRgb(btnSuccessBg)}, 0.25) !important;
    }
    
    .btn-danger:focus,
    .btn-danger.focus {
      box-shadow: 0 0 0 0.2rem rgba(${hexToRgb(btnDangerBg)}, 0.25) !important;
    }
    
    .btn-warning:focus,
    .btn-warning.focus {
      box-shadow: 0 0 0 0.2rem rgba(${hexToRgb(btnWarningBg)}, 0.25) !important;
    }
    
    .btn-info:focus,
    .btn-info.focus {
      box-shadow: 0 0 0 0.2rem rgba(${hexToRgb(btnInfoBg)}, 0.25) !important;
    }

    /* Text primary color links */
    .text-primary,
    a.text-primary {
      color: ${primaryColor} !important;
    }
    
    a:hover,
    a:focus,
    .nav-link:hover,
    .nav-link:focus {
      color: ${primaryColor} !important;
    }
  `;

  // Direct DOM styling for immediate visual feedback
  setTimeout(() => {
    // Apply to sidebar
    const sidebar = document.querySelector('.sidebar-navigation');
    if (sidebar) {
      sidebar.style.backgroundColor = sidebarBgColor;

      // Style links and text
      const menuLinks = sidebar.querySelectorAll('.menu-link:not(.active)');
      menuLinks.forEach(link => {
        link.style.color = sidebarTextColor;
      });

      // Style active items
      const activeItems = sidebar.querySelectorAll('.menu-item.active, .active');
      activeItems.forEach(item => {
        item.style.backgroundColor = sidebarActiveBgColor;

        // Style all children of active items
        const children = item.querySelectorAll('*');
        children.forEach(child => {
          child.style.color = sidebarActiveTextColor;
        });
      });
    }

    // Apply to card headers only (buttons are now handled by CSS only)
    const cardHeaders = document.querySelectorAll('.card-header');
    cardHeaders.forEach(header => {
      header.style.backgroundColor = blockTitleBgColor;
      header.style.color = blockTitleTextColor;
    });
  }, 100);

};

export const SettingsProvider = ({ children }) => {
  // Start with defaults - no forcing purple theme
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const loadingRef = useRef(false);
  const lastLoadTimeRef = useRef(0);
  const lastAppliedSettingsRef = useRef(null);

  // Check if we're on a public route
  const isPublicRoute =
    location.pathname === '/landing' ||
    location.pathname === '/register' ||
    location.pathname === '/login' ||
    location.pathname === '/forgot-password';

  // Load settings from API with throttling (no more than once every 5 seconds)
  const loadSettings = async () => {

    // Check if we've made a request recently (within 5 seconds)
    const now = Date.now();
    const timeSinceLastLoad = now - lastLoadTimeRef.current;
    if (timeSinceLastLoad < 5000 && lastLoadTimeRef.current > 0) {
      return;
    }

    if (loadingRef.current) {
      return;
    }

    setLoading(true);
    loadingRef.current = true;

    try {
      // Step 1: First apply default settings
      const initialSettings = { ...defaultSettings };

      // Step 2: Try to get user info
      let userRole = 'anonymous';
      let organizationId = null;
      let tenantId = null;

      try {
        const userStr = sessionStorage.getItem('user');

        if (userStr) {
          const user = JSON.parse(userStr);
          userRole = user.role || 'anonymous';
          organizationId = user.organization_id;
          tenantId = user.tenant_id || organizationId; // Prioritize tenant_id if available

          // Save for later reference
          initialSettings.user_org_id = organizationId;
          initialSettings.user_tenant_id = tenantId;
          initialSettings.user_role = userRole;
        }
      } catch (error) {
        console.error('SettingsContext: Error parsing user from session storage:', error);
      }

      // Step 3: Always try to load from API
      try {
        const publicResponse = await api.get('/api/settings/public');
        const publicSettings = publicResponse.data;

        // Reduced logging - only log if there's an issue
        if (publicSettings && Object.keys(publicSettings).length > 0) {
          // Check if we got meaningful data (not all null values)
          const hasRealData = Object.values(publicSettings).some(value => value !== null && value !== undefined && value !== '');

          if (hasRealData) {
            Object.keys(publicSettings).forEach(key => {
              if (publicSettings[key] !== null && publicSettings[key] !== undefined) {
                initialSettings[key] = publicSettings[key];
              }
            });

            // Check if we have any button color settings from the API
            const hasButtonSettings = Object.keys(publicSettings).some(key =>
              key.startsWith('btn_') && publicSettings[key] !== null && publicSettings[key] !== undefined
            );

            if (!hasButtonSettings) {
              // Only apply default button colors if none were retrieved from API
              initialSettings.btn_primary_bg = initialSettings.theme_primary_color || '#3D348B';
              initialSettings.btn_primary_text = '#ffffff';
              initialSettings.btn_primary_hover_bg = '#2b2670';
              initialSettings.btn_secondary_bg = '#6c757d';
              initialSettings.btn_secondary_text = '#ffffff';
              initialSettings.btn_secondary_hover_bg = '#5a6268';
              initialSettings.btn_success_bg = '#198754';
              initialSettings.btn_success_text = '#ffffff';
              initialSettings.btn_success_hover_bg = '#157347';
              initialSettings.btn_danger_bg = '#dc3545';
              initialSettings.btn_danger_text = '#ffffff';
              initialSettings.btn_danger_hover_bg = '#bb2d3b';
              initialSettings.btn_warning_bg = '#ffc107';
              initialSettings.btn_warning_text = '#000000';
              initialSettings.btn_warning_hover_bg = '#ffca2c';
              initialSettings.btn_info_bg = '#0dcaf0';
              initialSettings.btn_info_text = '#000000';
              initialSettings.btn_info_hover_bg = '#31d2f2';
              initialSettings.btn_outline_border = initialSettings.theme_primary_color || '#3D348B';
              initialSettings.btn_outline_text = initialSettings.theme_primary_color || '#3D348B';
              initialSettings.btn_outline_hover_bg = initialSettings.theme_primary_color || '#3D348B';
              initialSettings.btn_outline_hover_text = '#ffffff';
              initialSettings.btn_disabled_bg = '#e9ecef';
              initialSettings.btn_disabled_text = '#6c757d';
              initialSettings.btn_disabled_border = '#e9ecef';
            }
          } else {
            // Force purple theme even with defaults
            initialSettings.theme_primary_color = '#3D348B';
            initialSettings.primary_color = '#3D348B';
            initialSettings.sidebar_active_bg_color = '#3D348B';
            initialSettings.sidebar_hover_text_color = '#3D348B';
            // Force complete button color settings
            initialSettings.btn_primary_bg = '#3D348B';
            initialSettings.btn_primary_text = '#ffffff';
            initialSettings.btn_primary_hover_bg = '#2b2670';
            initialSettings.btn_secondary_bg = '#6c757d';
            initialSettings.btn_secondary_text = '#ffffff';
            initialSettings.btn_secondary_hover_bg = '#5a6268';
            initialSettings.btn_success_bg = '#198754';
            initialSettings.btn_success_text = '#ffffff';
            initialSettings.btn_success_hover_bg = '#157347';
            initialSettings.btn_danger_bg = '#dc3545';
            initialSettings.btn_danger_text = '#ffffff';
            initialSettings.btn_danger_hover_bg = '#bb2d3b';
            initialSettings.btn_warning_bg = '#ffc107';
            initialSettings.btn_warning_text = '#000000';
            initialSettings.btn_warning_hover_bg = '#ffca2c';
            initialSettings.btn_info_bg = '#0dcaf0';
            initialSettings.btn_info_text = '#000000';
            initialSettings.btn_info_hover_bg = '#31d2f2';
            initialSettings.btn_outline_border = '#3D348B';
            initialSettings.btn_outline_text = '#3D348B';
            initialSettings.btn_outline_hover_bg = '#3D348B';
            initialSettings.btn_outline_hover_text = '#ffffff';
            initialSettings.btn_disabled_bg = '#e9ecef';
            initialSettings.btn_disabled_text = '#6c757d';
            initialSettings.btn_disabled_border = '#e9ecef';

            setSettings(initialSettings);
            applyThemeVariables(initialSettings);
          }
          setSettings(initialSettings);

          // Apply theme immediately
          applyThemeVariables(initialSettings);

          // Persist public settings to localStorage for fallback
          if (LOCAL_STORAGE_AVAILABLE) {
            try {
              localStorage.setItem('app_settings', JSON.stringify(initialSettings));
              Object.entries(initialSettings).forEach(([key, value]) => {
                if (value !== null && value !== undefined) {
                  localStorage.setItem(`app_${key}`, value.toString());
                }
              });
            } catch (err) {
              console.error('SettingsContext: Error saving to localStorage:', err);
            }
          }
        } else {
          // Force purple theme even with defaults
          initialSettings.theme_primary_color = '#3D348B';
          initialSettings.primary_color = '#3D348B';
          initialSettings.sidebar_active_bg_color = '#3D348B';
          initialSettings.sidebar_hover_text_color = '#3D348B';
          // Force complete button color settings
          initialSettings.btn_primary_bg = '#3D348B';
          initialSettings.btn_primary_text = '#ffffff';
          initialSettings.btn_primary_hover_bg = '#2b2670';
          initialSettings.btn_secondary_bg = '#6c757d';
          initialSettings.btn_secondary_text = '#ffffff';
          initialSettings.btn_secondary_hover_bg = '#5a6268';
          initialSettings.btn_success_bg = '#198754';
          initialSettings.btn_success_text = '#ffffff';
          initialSettings.btn_success_hover_bg = '#157347';
          initialSettings.btn_danger_bg = '#dc3545';
          initialSettings.btn_danger_text = '#ffffff';
          initialSettings.btn_danger_hover_bg = '#bb2d3b';
          initialSettings.btn_warning_bg = '#ffc107';
          initialSettings.btn_warning_text = '#000000';
          initialSettings.btn_warning_hover_bg = '#ffca2c';
          initialSettings.btn_info_bg = '#0dcaf0';
          initialSettings.btn_info_text = '#000000';
          initialSettings.btn_info_hover_bg = '#31d2f2';
          initialSettings.btn_outline_border = '#3D348B';
          initialSettings.btn_outline_text = '#3D348B';
          initialSettings.btn_outline_hover_bg = '#3D348B';
          initialSettings.btn_outline_hover_text = '#ffffff';
          initialSettings.btn_disabled_bg = '#e9ecef';
          initialSettings.btn_disabled_text = '#6c757d';
          initialSettings.btn_disabled_border = '#e9ecef';

          setSettings(initialSettings);
          applyThemeVariables(initialSettings);
        }
      } catch (error) {
        // Fall back to localStorage if API fails
        if (LOCAL_STORAGE_AVAILABLE) {
          try {
            const cachedSettings = localStorage.getItem('app_settings');
            if (cachedSettings) {
              const parsedSettings = JSON.parse(cachedSettings);
              // Set the settings from cache
              const fallbackSettings = { ...initialSettings, ...parsedSettings };
              setSettings(fallbackSettings);

              // Apply theme settings
              applyThemeVariables(fallbackSettings);
            } else {
              setSettings(initialSettings);
              applyThemeVariables(initialSettings);
            }
          } catch (e) {
            setSettings(initialSettings);
            applyThemeVariables(initialSettings);
          }
        } else {
          // No localStorage, just use defaults 
          setSettings(initialSettings);
          applyThemeVariables(initialSettings);
        }
      }
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
      loadingRef.current = false;
      lastLoadTimeRef.current = Date.now();
    }
  };

  // Upload logo - skip on public routes
  const uploadLogo = async (file, isRemoval = false) => {
    if (isPublicRoute) {
      return { success: false, error: 'Cannot upload logo on public routes' };
    }

    try {
      if (isRemoval || file === null) {
        // Handle logo removal using main endpoint with action parameter
        console.log('Removing logo...');
        const formData = new FormData();
        formData.append('action', 'remove');

        const response = await api.post('/api/settings/logo', formData, {
          headers: {
            'Content-Type': undefined // Let browser set the correct multipart/form-data header
          }
        });

        if (response.status === 200) {
          await loadSettings(); // Reload all settings
          return { success: true, message: 'Logo removed successfully' };
        }
        return { success: false, error: 'Failed to remove logo' };
      } else {
        // Handle logo upload
        // console.log('Uploading logo file:', file.name, file.size, file.type);
        const formData = new FormData();
        formData.append('logo', file);

        // console.log('Sending logo upload request to /api/settings/logo');
        const response = await api.post('/api/settings/logo', formData, {
          headers: {
            'Content-Type': undefined // Let browser set the correct multipart/form-data header
          }
        });

        // console.log('Logo upload response status:', response.status);
        // console.log('Logo upload response data:', response.data);

        if (response.status === 200 && response.data?.logo_url) {
          // console.log('Logo upload successful, new URL:', response.data.logo_url);
          await loadSettings(); // Reload all settings
          return { success: true, logo_url: response.data.logo_url };
        } else {
          console.error('Logo upload failed - invalid response:', response);
          return { success: false, error: response.data?.message || 'Failed to upload logo' };
        }
      }
    } catch (err) {
      console.error('Error with logo operation:', err);
      if (err.response) {
        console.error('Error response status:', err.response.status);
        console.error('Error response data:', err.response.data);
        return { success: false, error: err.response.data?.message || err.message };
      }
      return { success: false, error: err.message };
    }
  };

  // Upload logo - skip on public routes
  const uploadScanner = async (file, isRemoval = false) => {
    if (isPublicRoute) {
      return { success: false, error: 'Cannot upload logo on public routes' };
    }

    try {
      if (isRemoval || file === null) {
        // Handle scanner removal using main endpoint with action parameter
        const formData = new FormData();
        formData.append('action', 'remove');

        const response = await api.post('/api/settings/scanner', formData, {
          headers: {
            'Content-Type': undefined // Let browser set the correct multipart/form-data header
          }
        });

        if (response.status === 200) {
          await loadSettings(); // Reload all settings
          return { success: true, message: 'Scanner removed successfully' };
        }
        return { success: false, error: 'Failed to remove Scanner' };
      } else {
        // Handle logo upload
        // console.log('Uploading logo file:', file.name, file.size, file.type);
        const formData = new FormData();
        formData.append('scanner', file);

        // console.log('Sending logo upload request to /api/settings/logo');
        const response = await api.post('/api/settings/scanner', formData, {
          headers: {
            'Content-Type': undefined // Let browser set the correct multipart/form-data header
          }
        });

        // console.log('Logo upload response status:', response.status);
        // console.log('Logo upload response data:', response.data);

        if (response.status === 200 && response.data?.scanner_qr_url) {
          await loadSettings(); // Reload all settings
          return { success: true, scanner_qr_url: response.data.scanner_qr_url };
        } else {
          console.error('Scanner upload failed - invalid response:', response);
          return { success: false, error: response.data?.message || 'Failed to upload logo' };
        }
      }
    } catch (err) {
      console.error('Error with logo operation:', err);
      if (err.response) {
        console.error('Error response status:', err.response.status);
        console.error('Error response data:', err.response.data);
        return { success: false, error: err.response.data?.message || err.message };
      }
      return { success: false, error: err.message };
    }
  };

  // Reset settings to defaults - skip on public routes
  const resetSettings = async () => {
    if (isPublicRoute) {
      return { success: false, error: 'Cannot reset settings on public routes' };
    }

    try {
      await api.post('/api/settings/reset');

      // Apply defaults immediately
      setSettings(defaultSettings);
      applyThemeVariables(defaultSettings);

      // Save defaults to localStorage for fallback
      if (LOCAL_STORAGE_AVAILABLE) {
        try {
          localStorage.setItem('app_settings', JSON.stringify(defaultSettings));
          Object.entries(defaultSettings).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
              localStorage.setItem(`app_${key}`, value.toString());
            }
          });
        } catch (err) {
          console.error('Error saving default settings to localStorage:', err);
        }
      }

      // Then reload from API
      await loadSettings();
      return { success: true };
    } catch (err) {
      console.error('Error resetting settings:', err);
      return { success: false, error: err.message };
    }
  };

  // Save settings to API - skip on public routes
  const saveSettings = async (newSettings) => {
    if (isPublicRoute) {
      return { success: false, error: 'Cannot save settings on public routes' };
    }

    try {
      setLoading(true);
      setError(null);

      // Check user authentication 
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        console.error('Authorization token missing - cannot save settings');
        throw new Error('You must be logged in to save settings');
      }

      // Get user data from token to confirm organization_id and tenant_id
      let userData;
      try {
        userData = JSON.parse(atob(token.split('.')[1]));
        // console.log('User data from token:', userData);
        if (!userData.organization_id && userData.organization_id !== 0) {
          console.error('Organization ID is missing in the token data');
          throw new Error('Your account is not associated with an organization');
        }
      } catch (tokenError) {
        console.error('Error extracting data from token:', tokenError);
        throw new Error('Could not verify your organization - please log in again');
      }

      // Handle different input types (FormData vs Object)
      let settingsToProcess = {};

      if (newSettings instanceof FormData) {
        // Convert FormData to object
        for (const [key, value] of newSettings.entries()) {
          if (key && !key.startsWith('_') && !['logoFile', 'logo_file'].includes(key)) {
            settingsToProcess[key] = value;
          }
        }
        // console.log('Converted FormData to object with', Object.keys(settingsToProcess).length, 'settings');
      } else if (typeof newSettings === 'object' && newSettings !== null) {
        // Filter the object
        Object.entries(newSettings).forEach(([key, value]) => {
          if (key && !key.startsWith('_') && !['logoFile', 'logo_file'].includes(key) &&
            !(!isNaN(parseInt(key)) && String(parseInt(key)) === key)) {
            settingsToProcess[key] = value;
          }
        });
        // console.log('Filtered object with', Object.keys(settingsToProcess).length, 'settings');
      } else {
        throw new Error('Invalid settings format provided');
      }

      // Add organization_id from token if not present
      if (userData && userData.organization_id && !settingsToProcess.organization_id) {
        settingsToProcess.organization_id = userData.organization_id;
        // console.log(`Adding organization_id ${userData.organization_id} from token to settings`);
      }

      // Also add tenant_id equal to organization_id to ensure compatibility
      if (userData && userData.organization_id && !settingsToProcess.tenant_id) {
        settingsToProcess.tenant_id = userData.organization_id;
        // console.log(`Adding tenant_id ${userData.organization_id} (equal to organization_id) to settings`);
      }

      // If token has tenant_id, use that (might be different from organization_id)
      if (userData && userData.tenant_id && userData.tenant_id !== userData.organization_id) {
        settingsToProcess.tenant_id = userData.tenant_id;
        // console.log(`Using tenant_id ${userData.tenant_id} from token (different from organization_id)`);
      }

      // Debug for maintenance_fee
      if (settingsToProcess.maintenance_fee !== undefined) {
        // Debug logging removed
      }

      // console.log('Sending settings update to server:', Object.keys(settingsToProcess).length, 'settings');

      // Process settings array for API
      const settingsArray = Object.entries(settingsToProcess).map(([key, value]) => {
        const valueType = typeof value;
        // Convert null values to empty strings
        const processedValue = value === null ? '' : value;

        return {
          key,
          value: processedValue,
          type: valueType === 'object' ? 'json' : valueType
        };
      });

      console.log('saveSettings - Saving', settingsArray.length, 'settings to server');

      const response = await api.put('/api/settings', { settings: settingsArray });

      if (response.status === 200) {
        // console.log('Settings saved successfully, reloading...');

        // Immediately update local settings
        const updatedSettings = { ...settings };
        settingsArray.forEach(({ key, value }) => {
          updatedSettings[key] = value;
        });

        setSettings(updatedSettings);
        applyThemeVariables(updatedSettings);

        // Save to localStorage as backup
        if (LOCAL_STORAGE_AVAILABLE) {
          try {
            localStorage.setItem('app_settings', JSON.stringify(updatedSettings));
            settingsArray.forEach(({ key, value }) => {
              localStorage.setItem(`app_${key}`, String(value));
            });
          } catch (err) {
            console.error('Error saving settings to localStorage:', err);
          }
        }

        // Don't reload from API after save to prevent infinite loops
        // The settings are already updated locally above

        return { success: true };
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err);
      setLoading(false);
      return { success: false, error: err.message };
    }
  };

  // Add a new function to force refresh settings directly from API
  const forceReloadSettings = async () => {
    // console.log('Force reloading settings from API...');

    // Reset last load time to force immediate reload
    lastLoadTimeRef.current = 0;

    // Clear localStorage cache first
    if (LOCAL_STORAGE_AVAILABLE) {
      try {
        localStorage.removeItem('app_settings');
        // Also clear individual settings
        for (const key in localStorage) {
          if (key.startsWith('app_')) {
            localStorage.removeItem(key);
          }
        }
        // console.log('Cleared all settings cache for force reload');
      } catch (e) {
        console.warn('Could not clear localStorage settings:', e);
      }
    }

    // Force reload from API
    return loadSettings();
  };

  // Apply default theme variables immediately on mount
  useEffect(() => {
    if (settings && Object.keys(settings).length > 0) {
      // Check if settings have actually changed to prevent infinite loops
      const settingsString = JSON.stringify(settings);
      if (lastAppliedSettingsRef.current !== settingsString) {
        applyThemeVariables(settings);
        lastAppliedSettingsRef.current = settingsString;
      }
    }
  }, [settings]);

  // Load settings from API on mount - but only once
  useEffect(() => {
    // Skip loading on public routes
    if (!isPublicRoute) {
      // Only load once when the component mounts, not on every render
      const hasLoaded = loadingRef.current || lastLoadTimeRef.current > 0;
      if (!hasLoaded) {
        console.log('SettingsProvider: Loading settings for the first time');
        loadSettings();
      }
    }
  }, [isPublicRoute]); // Only depend on isPublicRoute, not on loadSettings function

  return (
    <SettingsContext.Provider value={{
      settings,
      loading,
      error,
      saveSettings,
      loadSettings,
      forceReloadSettings,
      uploadLogo,
      uploadScanner,
      resetSettings,
      formatCurrency
    }}>
      {children}
    </SettingsContext.Provider>
  );
};

export default SettingsContext; 